<script lang="ts">
  import { icon } from '#app/lib/icons'
  import { imgSrc } from '#app/lib/image_source'
  import { onChange } from '#app/lib/svelte/svelte'
  import { isOpenedOutside } from '#app/lib/utils'
  import { getGroupMembersCount, getGroupPicture, type SerializedGroup } from '#groups/lib/groups'
  import type { OnSelectDoc } from '#inventory/types/inventory_browser'
  import { i18n, I18n } from '#user/lib/i18n'
  import { getPicture as getUserPicture, type SerializedUser } from '#users/lib/users'

  const {
    doc,
    group,
    onSelectDoc,
  }: {
    doc: SerializedUser | SerializedGroup
    group?: SerializedGroup
    onSelectDoc: OnSelectDoc
  } = $props()

  const type = $derived('type' in doc && doc.type === 'group' ? 'group' : 'user')

  let name: string = $state()
  let username: string = $state()
  let hasItemsCount: boolean = $state()
  let itemsCount: number = $state()
  const pathname = $derived(doc.pathname)

  let picture: (SerializedUser | SerializedGroup)['picture'] = $state()
  let membersCount: number = $state()
  $effect(() => {
    onChange(doc, () => {
      if ('type' in doc && doc.type === 'group') {
        membersCount = getGroupMembersCount(doc)
        picture = getGroupPicture(doc)
        name = doc.name
      } else {
        picture = getUserPicture(doc)
        username = doc.username
        itemsCount = doc.itemsCount
        hasItemsCount = doc.itemsCount != null
      }
    })
  })

  const userIsGroupAdmin = $derived.by(() => {
    if (group) {
      return group.admins.find(({ user }) => user === doc._id) != null
    } else {
      return false
    }
  })

  function select (e) {
    if (isOpenedOutside(e)) return
    if (type === 'group') {
      onSelectDoc({ doc, type } as { type: 'group', doc: SerializedGroup })
    } else {
      onSelectDoc({ doc, type } as { type: 'user', doc: SerializedUser })
    }
    e.preventDefault()
  }
</script>

<a
  class="nav-list-element"
  href={pathname}
  onclick={select}
>
  <div class="picture" style:background-image="url({imgSrc(picture, 100)})"></div>
  {#if type === 'group'}
    <div class="members-count">{@html icon('group')} {membersCount}</div>
  {:else}
    {#if hasItemsCount}
      <div class="items-count" title={i18n('num_books', { smart_count: itemsCount })}>{@html icon('book')} {itemsCount}</div>
    {/if}
  {/if}

  {#if userIsGroupAdmin}
    <div class="group-admin-badge" title={I18n('group admin')}>{@html icon('cog')}</div>
  {/if}

  <div class="info">
    {#if type === 'group'}
      <strong>{name}</strong>
    {:else}
      <strong>{username}</strong>
    {/if}
  </div>
</a>

<style lang="scss">
  @import "general/scss/utils";
  .nav-list-element{
    position: relative;
    display: block;
    height: $users-home-nav-avatar-size;
    width: $users-home-nav-avatar-size;
    .items-count, .members-count{
      opacity: 0.6;
    }
    @include radius;
    &:hover{
      .items-count, .members-count{
        opacity: 1;
      }
    }
  }
  .picture{
    background-size: cover;
    background-position: center center;
    @include position(absolute, 0, 0, 0, 0);
  }
  .info{
    @include position(absolute, null, 0, 0, 0);
    overflow: hidden;
    height: 1.6em;
    background-color: white;
    padding: 0 0.2em;
  }
  .items-count, .group-admin-badge, .members-count{
    position: absolute;
    background-color: white;
    color: $dark-grey;
    line-height: 0;
    min-width: 1em;
  }
  .items-count, .members-count{
    inset-block-start: 0;
    inset-inline-end: 0;
    text-align: center;
    padding: 0.2em 0;
    border-end-start-radius: $global-radius;
    @include transition;
  }
  .group-admin-badge{
    inset-block-start: 0;
    inset-inline-start: 0;
    // Somehow centers the icon vertically
    line-height: 0;
    border-end-end-radius: $global-radius;
  }
</style>
